import type { EpisodeDto, TvShowDto, WbsClient } from '@bldsoft/cappie/api/wbs'
import type {
  API,
  CategoryWithShowsApi,
  MediaUrl,
  Page,
  PlaylistSeason,
  TvShow,
  TvShowCategory,
  TvShowEpisode,
  TvShowSeason,
} from '@setplex/tria-api'
import type { AdapterDefaults } from '../../index.h'

import { ensureOk, payload } from '@bldsoft/cappie/rest'
import {
  RECOMMENDED_AMOUNT_SHOWS,
  RECOMMENDED_AMOUNT_SHOWS_AND_CURRENT,
} from '@setplex/tria-api'
import { sample } from 'effector'
import { subscriberCache } from '../../cache'
import { DEFAULT_PAGE_SIZE } from '../../constants/generic'
import { getFirstPagePaginateInfo } from '../../utils/pageInfo'
import {
  rememberCompletePage,
  reuseFirstBySortOrder,
} from '../../utils/pageReuse'
import {
  formatEpisode,
  formatSeason,
  formatSeasonPlaylist,
  formatShow,
  formatTvShowUpdatedOnly,
} from './tvshows.format'

/**
 * Show pages request the same seasons and episodes several times while they
 * assemble, so those few calls are cached for a short while. Player launch
 * then asks for page 0, count 1; a complete first page is reused for that probe.
 */
const SHORT_TTL = 10_000

export function use(
  client: WbsClient,
  tvshows: API['tvshows'],
  _api: API,
  defaults: AdapterDefaults
): void {
  // GET /v3/tvshows?sort-by=updatedTime&sort-order=desc
  tvshows.getLastAddedTvShowsFx.use(
    async ({
      page,
      count = defaults.count || DEFAULT_PAGE_SIZE,
      categoryId,
      collectionId,
      onlyFavorites,
    }) => {
      // however name is 'LastAdded' - 'sort-by=updatedTime' is default
      const shows = await payload(
        client.get.api.v3.tvshows({
          query: {
            page,
            count,
            categoryId: categoryId || undefined,
            collectionId,
            'only-favorites': onlyFavorites,
            'sort-by': 'updatedTime',
            'sort-order': 'desc',
          },
        })
      )
      return {
        ...shows,
        content: shows.content.map(formatShow),
      }
    }
  )

  // GET /v3/tvshows
  tvshows.getAllTvShowsFx.use(
    async ({
      page,
      count = defaults.count || DEFAULT_PAGE_SIZE,
      categoryId,
      sortOrder,
      sortBy,
      q,
      onlyFavorites,
      ids,
    }) => {
      const shows = await payload(
        client.get.api.v3.tvshows({
          query: {
            page,
            count,
            'sort-by': sortBy || undefined,
            'sort-order': sortOrder || undefined,
            q: q || undefined,
            categoryId,
            'only-favorites': onlyFavorites || undefined,
            ids,
          },
        })
      )

      return {
        ...shows,
        content: shows.content.map(formatShow),
      }
    }
  )

  // GET /v3/library/tvshows
  tvshows.getPurchasedTvShowsFx.use(
    async ({
      page = 0,
      count = defaults.count || 36,
      rented = true,
      purchased = true,
    }) => {
      const shows = await payload(
        client.get.api.v3.library.tvshows({
          query: {
            page,
            count,
            rented,
            purchased,
          },
        })
      )

      return {
        ...shows,
        content: shows.content.map(formatShow),
      }
    }
  )

  // GET /v3/tvshows?ids=tvShowId
  tvshows.getTvShowBaseFx.use(async ({ tvShowId }) => {
    // without pagination, other params will be ignored
    const shows = (await payload(
      client.get.api.v3.tvshows({
        query: { ids: tvShowId },
        meta: { cache: subscriberCache(SHORT_TTL) },
      })
    )) as unknown as TvShowDto[]

    return shows.map(formatShow)
  })

  // GET /v3/tvshows?categoryId=categoryId
  tvshows.getShowsByCategoryIdFx.use(
    async ({
      page,
      count = defaults.count || DEFAULT_PAGE_SIZE,
      categoryId,
    }) => {
      const shows = await payload(
        client.get.api.v3.tvshows({
          query: { page, count, categoryId: categoryId || undefined },
        })
      )

      return {
        ...shows,
        content: shows.content.map(formatShow),
      }
    }
  )

  // GET /v3/tvshows?&updatedTimeOnly=true
  tvshows.getTvShowsUpdatedOnlyFx.use(async () => {
    const shows = (await payload(
      client.get.api.v3.tvshows({ query: { updatedTimeOnly: true } })
    )) as unknown as TvShowDto[]

    return shows.map(formatTvShowUpdatedOnly)
  })

  // GET /v3/tvshows/categories
  tvshows.getTvShowCategoriesFx.use(
    async ({
      page,
      count = defaults.count || DEFAULT_PAGE_SIZE,
      sortOrder = defaults.sortOrder || 'asc',
    }) => {
      const categories = await payload(
        client.get.api.v3.tvshows.categories({
          query: { page, count, 'sort-order': sortOrder || undefined },
        })
      )

      return categories
    }
  )

  // GET /v3/tvshows/categories/{categoryId}/subcategories
  tvshows.getTvShowSubcategoriesByCategoryFx.use(
    async ({
      categoryId,
      page,
      count = defaults.count || DEFAULT_PAGE_SIZE,
      sortOrder = defaults.sortOrder || 'asc',
    }) => {
      const categories = await payload(
        client.get.api.v3.tvshows.categories(categoryId).subcategories({
          query: { page, count, 'sort-order': sortOrder || undefined },
        })
      )

      return categories
    }
  )

  // GET /v3/tvshows/{tvShowId}/trailer/url
  tvshows.getTvShowTrailerUrlFx.use(async ({ tvShowId }) => {
    const media = await payload(
      client.get.api.v3.tvshows(tvShowId).trailer.url()
    )

    return media as MediaUrl
  })

  // PATCH /v3/tvshows/{tvShowId}
  tvshows.updateTvShowFx.use(async ({ id, favorite }) => {
    const body: { id?: number; favorite?: boolean } = {}
    if (id != null) body.id = Number(id)
    if (favorite != null) body.favorite = Boolean(favorite)
    await ensureOk(client.patch.api.v3.tvshows(id, { body }))
  })

  // PUT /v3/tvshows/{tvShowId}/seasons/{seasonId}/episodes/{episodeId}/continue-watching
  tvshows.updateContinueWatchingTvShowFx.use(
    async ({ tvShowId, seasonId, episodeId, stoppedTime, lengthMillis }) => {
      const body: { stoppedTime: number; lengthMillis?: number } = {
        stoppedTime: 0,
      }

      // FIXME: why is it here?
      if (stoppedTime == null)
        throw new Error(
          'Empty stoppedTime arg in updateContinueWatchingTvShowFx'
        )

      body.stoppedTime = Number(stoppedTime)
      if (lengthMillis) body.lengthMillis = Number(lengthMillis)

      await ensureOk(
        client.put.api.v3
          .tvshows(tvShowId)
          .seasons(seasonId)
          .episodes(episodeId)
          .continueWatching({ body })
      )
    }
  )

  tvshows.removeContinueWatchingTvShowFx.use(
    async ({ tvShowId, seasonId, episodeId }) => {
      await ensureOk(
        client.delete.api.v3
          .tvshows(tvShowId)
          .seasons(seasonId)
          .episodes(episodeId)
          .continueWatching()
      )
    }
  )

  // PUT /v3/tvshows/{tvShowId}/episodes/{episodeId}/continue-watching
  tvshows.updateContinueWatchingWithoutSeasonFx.use(
    async ({ tvShowId, episodeId, stoppedTime, lengthMillis }) => {
      const body: { stoppedTime: number; lengthMillis?: number } = {
        stoppedTime: 0,
      }

      // FIXME: why is it here?
      if (stoppedTime == null)
        throw new Error(
          'Empty stoppedTime arg in updateContinueWatchingWithoutSeasonFx'
        )

      body.stoppedTime = Number(stoppedTime)
      if (lengthMillis) body.lengthMillis = Number(lengthMillis)

      await ensureOk(
        client.put.api.v3
          .tvshows(tvShowId)
          .episodes(episodeId)
          .continueWatching({ body })
      )
    }
  )

  // DELETE /v3/tvshows/{tvShowId}/episodes/{episodeId}/continue-watching
  tvshows.removeContinueWatchingWithoutSeasonFx.use(
    async ({ tvShowId, episodeId }) => {
      await ensureOk(
        client.delete.api.v3
          .tvshows(tvShowId)
          .episodes(episodeId)
          .continueWatching()
      )
    }
  )

  // GET /v3/tvshows/{tvShowId}/seasons/{seasonId}/episodes/{episodeId}
  tvshows.getTvShowEpisodeBaseFx.use(
    async ({ tvShowId, seasonId, episodeId }) => {
      const episode = await payload(
        client.get.api.v3
          .tvshows(tvShowId)
          .seasons(seasonId)
          .episodes(episodeId)()
      )
      return formatEpisode(episode)
    }
  )

  // GET /v3/tvshows/{tvShowId}/episodes/{episodeId}
  tvshows.getTvShowEpisodeWithoutSeasonBaseFx.use(
    async ({ tvShowId, episodeId }) => {
      const episode = await payload(
        client.get.api.v3.tvshows(tvShowId).episodes(episodeId)()
      )
      return episode as TvShowEpisode //format
    }
  )

  // GET /v3/tvshows/{tvShowId}/season/{seasonId}/episodes
  tvshows.getTvShowEpisodesBySeasonByPageBaseFx.use(
    async ({
      tvShowId,
      seasonId,
      page,
      sortBy = 'updatedTime',
      count = defaults.count || DEFAULT_PAGE_SIZE,
      sortOrder = defaults.sortOrder || 'asc',
    }) => {
      const anyFullPageKey = `season-episodes:${tvShowId}:${seasonId}`
      const reused = reuseFirstBySortOrder<TvShowEpisode>(
        anyFullPageKey,
        page,
        count
      )
      if (reused) return reused

      const query = {
        page,
        'sort-by': sortBy || undefined,
        count: count || undefined,
        'sort-order': sortOrder || undefined,
      }

      const episodes = await payload(
        client.get.api.v3
          .tvshows(tvShowId)
          .seasons(seasonId)
          .episodes({ query, meta: { cache: subscriberCache(SHORT_TTL) } })
      )

      const pageData = {
        ...episodes,
        content: episodes.content.map(formatEpisode),
      }
      rememberCompletePage(anyFullPageKey, page, pageData, SHORT_TTL)
      return pageData
    }
  )

  // GET /v3/tvshows/{tvShowId}/episodes
  tvshows.getTvShowEpisodesWithoutSeasonByPageBaseFx.use(
    async ({
      tvShowId,
      page,
      count = defaults.count || DEFAULT_PAGE_SIZE,
      sortBy = 'updatedTime',
      sortOrder = defaults.sortOrder || 'asc',
    }) => {
      const anyFullPageKey = `episodes:${tvShowId}`
      const reused = reuseFirstBySortOrder<TvShowEpisode>(
        anyFullPageKey,
        page,
        count
      )
      if (reused) return reused

      const query = {
        page,
        count: count || undefined,
        'sort-by': sortBy || undefined,
        'sort-order': sortOrder || undefined,
      }

      const episodes = await payload(
        client.get.api.v3
          .tvshows(tvShowId)
          .episodes({ query, meta: { cache: subscriberCache(SHORT_TTL) } })
      )
      const pageData = {
        ...episodes,
        content: episodes.content.map(formatEpisode),
      }
      rememberCompletePage(anyFullPageKey, page, pageData, SHORT_TTL)
      return pageData
    }
  )

  // GET /v3/tvshows/{tvShowId}/seasons/{seasonId}
  tvshows.getTvShowSeasonByIdBaseFx.use(async ({ tvShowId, seasonId }) => {
    const season = await payload(
      client.get.api.v3.tvshows(tvShowId).seasons(seasonId)({
        meta: { cache: subscriberCache(SHORT_TTL) },
      })
    )
    return formatSeason(season)
  })

  // GET /v3/tvshows/{tvShowId}/seasons
  tvshows.getTvShowSeasonsByPageBaseFx.use(
    async ({
      tvShowId,
      page,
      count = defaults.count || DEFAULT_PAGE_SIZE,
      sortBy = 'updatedTime',
      sortOrder = defaults.sortOrder || 'asc',
    }) => {
      const anyFullPageKey = `seasons:${tvShowId}`
      const reused = reuseFirstBySortOrder<TvShowSeason>(
        anyFullPageKey,
        page,
        count
      )
      if (reused) return reused

      const query = {
        page,
        count: count || undefined,
        'sort-by': sortBy || undefined,
        'sort-order': sortOrder || undefined,
      }

      const seasons = await payload(
        client.get.api.v3
          .tvshows(tvShowId)
          .seasons({ query, meta: { cache: subscriberCache(SHORT_TTL) } })
      )

      const pageData = {
        ...seasons,
        content: seasons.content.map(formatSeason),
      }
      rememberCompletePage(anyFullPageKey, page, pageData, SHORT_TTL)
      return pageData
    }
  )

  // GET /v3/tvshows/{tvShowId}/seasons/{seasonId}/episodes/{episodeId}/url
  tvshows.getTvShowEpisodeUrlFx.use(
    async ({ tvShowId, seasonId, episodeId }) => {
      const media = await payload(
        client.get.api.v3
          .tvshows(tvShowId)
          .seasons(seasonId)
          .episodes(episodeId)
          .url()
      )
      return media as MediaUrl
    }
  )

  // GET /v3/tvshows/{tvShowId}/episodes/{episodeId}/url
  tvshows.getEpisodeUrlWithoutSeasonFx.use(async ({ tvShowId, episodeId }) => {
    const media = await payload(
      client.get.api.v3.tvshows(tvShowId).episodes(episodeId).url()
    )
    return media as MediaUrl
  })

  // POST /v3/tvshows/{tvShowId}/seasons/{seasonId}/episodes/{episodeId}/watched
  tvshows.postMarkEpisodeWatchedFx.use(
    async ({ tvShowId, seasonId, episodeId, watched }) => {
      await ensureOk(
        client.post.api.v3
          .tvshows(tvShowId)
          .seasons(seasonId)
          .episodes(episodeId)
          .watched({ body: { watched } })
      )
    }
  )

  // POST /v3/tvshows/{tvShowId}/episodes/{episodeId}/watched
  tvshows.postMarkEpisodeWatchedWithoutSeasonFx.use(
    async ({ tvShowId, episodeId, watched }) => {
      await ensureOk(
        client.post.api.v3
          .tvshows(tvShowId)
          .episodes(episodeId)
          .watched({ body: { watched } })
      )
    }
  )

  tvshows.getSeasonsWithEpisodesFx.use(async ({ tvShowId }) => {
    const currentTvShow = await tvshows.getCurrenShowByIdFx({
      tvShowId,
    })
    const { isWithSeason } = currentTvShow?.[0] || {}
    const getEpisodes = async ({
      seasonId,
      tvShowId,
    }: {
      seasonId?: number
      tvShowId: number
    }) =>
      await tvshows.getAllEpisodesBySeasonFx({
        seasonId,
        tvShowId,
      })

    if (!isWithSeason) {
      const episodes = await getEpisodes({ tvShowId })
      return { episodes }
    }

    const seasons = await tvshows.getAllSeasonsByShowIdFx({
      tvShowId,
    })

    const promises = seasons.map(async ({ id: seasonId, ...rest }) => {
      const episodes = await getEpisodes({ seasonId, tvShowId })
      return {
        ...rest,
        id: seasonId,
        episodes,
        episodesAmount: episodes.length,
      }
    })

    const seasonsWithEpisodes = await Promise.all(promises)
    return { seasonsWithEpisodes }
  })

  tvshows.getAllSeasonsFx.use(async ({ tvShowId }) => {
    const firstPageSeasons = await tvshows.getTvShowSeasonsByPageFx({
      tvShowId,
      page: 0,
    })

    let restSeasons: TvShowSeason[] = []
    if (!firstPageSeasons.last) {
      const pagesToFetch = Array.from(
        { length: firstPageSeasons.totalPages - 1 },
        (_, i) => i + 1
      )

      const promises = pagesToFetch.map((page) =>
        tvshows.getTvShowSeasonsByPageFx({ page: page, tvShowId })
      )
      const seasonsData = await Promise.all(promises)

      restSeasons = seasonsData.flatMap((page) => [...page.content])
    }
    return [...firstPageSeasons.content, ...restSeasons]
  })

  tvshows.getAllEpisodesFx.use(async ({ tvShowId, seasonId, ...rest }) => {
    // also works for seasonId = undefined
    const firstPageEpisodes = await tvshows.getTvShowEpisodesSmartFx({
      tvShowId,
      seasonId,
      page: 0,
      ...rest,
    })

    let restEpisodes: Array<TvShowEpisode> = []
    if (!firstPageEpisodes.last) {
      const pagesToFetch = Array.from(
        { length: firstPageEpisodes.totalPages - 1 },
        (_, i) => i + 1
      )

      const promises = pagesToFetch.map((page) =>
        tvshows.getTvShowEpisodesSmartFx({
          page: page,
          seasonId,
          tvShowId,
          ...rest,
        })
      )
      const episodesData: Array<Page<EpisodeDto>> = await Promise.all(promises)

      restEpisodes = episodesData
        .map((page) => page.content.map(formatEpisode))
        .reduce((acc, val) => acc.concat(val), [])
    }

    return [...firstPageEpisodes.content, ...restEpisodes]
  })

  tvshows.getAllEpisodesForSeasonsFx.use(async ({ seasons, tvShowId }) => {
    const promises = seasons.map(async (season) => {
      const episodes = await tvshows.getAllEpisodesFx({
        tvShowId,
        seasonId: season.id,
      })
      return { [season.id]: episodes }
    })

    const episodesData = await Promise.all(promises)

    const episodesBySeason = episodesData.reduce(
      (acc, val) => ({ ...acc, ...val }),
      {}
    )

    return episodesBySeason
  })

  tvshows.continueGenSeasonLessSeriesPlaylistFx.use(
    async ({ tvShowId, episodeId, isAutoplay, isLooped, categoryId }) => {
      const currentTvShow = (await tvshows.getTvShowBaseFx({ tvShowId }))[0]

      const allEpisodes = (
        await tvshows.getAllEpisodesFx({
          tvShowId,
        })
      ).map(formatEpisode)

      const currentEpisodeIndex = allEpisodes.findIndex(
        (ep) => ep.id === episodeId
      )

      // last episode in show case
      if (allEpisodes.length - 1 === currentEpisodeIndex) {
        return {
          nextEpisode: null, // null for last episode in show case
          currentEpisode: allEpisodes[currentEpisodeIndex],
          currentSeason: null,
          currentTvShow,
          episodes: allEpisodes,
          nextSeasonId: null,
          categoryId: Number(categoryId),
          isAutoplay,
          isLooped,
        }
      }

      const nextEpisode = allEpisodes[currentEpisodeIndex + 1]

      return {
        nextEpisode: nextEpisode || null,
        currentEpisode: allEpisodes[currentEpisodeIndex],
        currentSeason: null,
        currentTvShow,
        episodes: allEpisodes,
        nextSeasonId: null,
        categoryId: Number(categoryId),
        isAutoplay,
        isLooped,
      }
    }
  )

  tvshows.continueGenWithSeasonSeriesPlaylistFx.use(
    async ({
      tvShowId,
      seasonId,
      episodeId,
      isAutoplay,
      isLooped,
      categoryId,
    }) => {
      const currentTvShow = (await tvshows.getTvShowBaseFx({ tvShowId }))[0]

      const seasons = await tvshows.getAllSeasonsFx({
        tvShowId,
      })

      const episodesBySeason = await tvshows.getAllEpisodesForSeasonsFx({
        tvShowId,
        seasons: seasons.map((season) => ({ id: Number(season.id) })),
      })

      const seasonsWithEpisodes = seasons
        .map((season) => {
          const seasonId = Number(season.id)
          const seasonEpisodes = episodesBySeason[seasonId] || []

          return formatSeasonPlaylist({
            season,
            episodesAmount: seasonEpisodes.length,
            episodes: seasonEpisodes.map(formatEpisode),
          })
        })
        .filter((season) => season.episodesAmount)

      let currentSeason: PlaylistSeason
      currentSeason =
        seasonsWithEpisodes.find((season) =>
          season.episodes.some((episode) => episodeId === episode.id)
        ) || seasonsWithEpisodes[0]

      const currentEpisodes = currentSeason?.episodes || []

      const currentEpisode =
        currentEpisodes.find((episode) => episodeId === episode.id) ?? null

      const currentEpPosition = currentEpisodes.findIndex(
        (ep) => ep === currentEpisode
      )

      const isLastEpisode = currentEpisodes.length - 1 === currentEpPosition

      const isLastSeason =
        currentSeason.id ===
        seasonsWithEpisodes[seasonsWithEpisodes.length - 1].id

      // common case - next episode
      const nextEpisode = currentEpisodes[currentEpPosition + 1]

      if (isLastSeason && isLastEpisode) {
        return {
          nextEpisode: null,
          currentEpisode,
          currentSeason,
          currentTvShow,
          seasonsWithEpisodes,
          nextSeasonId: null,
          categoryId: Number(categoryId),
          isAutoplay,
          isLooped,
        }
      }

      if (isLastEpisode) {
        const currentSeasonIndex = seasonsWithEpisodes.findIndex(
          ({ id }) => currentSeason.id === id
        )
        const nextSeasonIndex =
          currentSeasonIndex !== -1 ? currentSeasonIndex + 1 : 0

        const nextSeason =
          seasonsWithEpisodes[nextSeasonIndex] &&
          seasonsWithEpisodes[nextSeasonIndex]!.episodes?.length
            ? seasonsWithEpisodes[nextSeasonIndex]
            : null
        // : seasonsWithEpisodes[nextSeasonIndex + 1]

        return {
          nextEpisode: nextSeason?.episodes?.[0] || null,
          currentEpisode,
          currentSeason,
          currentTvShow,
          seasonsWithEpisodes,
          nextSeasonId: nextSeason?.id ?? seasonId,
          categoryId: Number(categoryId),
          isAutoplay,
          isLooped,
        }
      }

      return {
        nextEpisode: nextEpisode || null,
        currentEpisode,
        currentSeason,
        currentTvShow,
        seasonsWithEpisodes,
        nextSeasonId: seasonId,
        categoryId: Number(categoryId),
        isAutoplay,
        isLooped,
      }
    }
  )

  tvshows.generateSeriesPlaylistFx.use(
    async ({
      tvShowId,
      seasonId,
      episodeId,
      categoryId,
      showIndex,
      ...rest
    }) => {
      const isSeasonLess = !seasonId && typeof seasonId !== 'number'

      if (isSeasonLess) {
        return await tvshows.continueGenSeasonLessSeriesPlaylistFx({
          tvShowId,
          episodeId,
          categoryId,
          showIndex,
          ...rest,
        })
      }

      return await tvshows.continueGenWithSeasonSeriesPlaylistFx({
        tvShowId,
        seasonId,
        episodeId,
        categoryId,
        showIndex,
        ...rest,
      })
    }
  )

  const formatToRecommendedShow = (categoryId: number) => (show: TvShow) => ({
    ...show,
    categoryId,
  })

  tvshows.getPlayerRecommendedTvShowsFx.use(async ({ tvShowId, count = 4 }) => {
    const shows = await payload(
      client.get.api.v3.tvshows({
        query: { 'recommended-to': tvShowId, count, page: 0 },
      })
    )
    return shows.content.map(formatShow)
  })

  tvshows.getRecommendedTvShowsFx.use(
    async ({ categoryId, collectionId, onlyFavorites, tvShowId, ...rest }) => {
      const formatShow = formatToRecommendedShow(categoryId)

      // FP-199
      const orderRecommended = (shows: readonly TvShow[]) => {
        // it is not possible to get exact Category data from b.e!!!!!
        const foundIndex = shows.findIndex((show) => show.id === tvShowId)
        const currentIndex = foundIndex === -1 ? 0 : foundIndex

        // ordering from FP-199
        const firstPart = shows.slice(
          currentIndex,
          RECOMMENDED_AMOUNT_SHOWS_AND_CURRENT
        )
        const secondPart = shows.slice(0, currentIndex)
        const orderedRecommended =
          currentIndex >= 0 ? [...firstPart, ...secondPart] : shows

        const filterNoCurrent = orderedRecommended
          .filter((show) => show.id !== tvShowId)
          .slice(0, RECOMMENDED_AMOUNT_SHOWS)

        return filterNoCurrent.map(formatShow)
      }

      if ('showIndex' in rest && typeof rest.showIndex === 'number') {
        const count = defaults.count || DEFAULT_PAGE_SIZE

        if (count <= RECOMMENDED_AMOUNT_SHOWS)
          throw new Error('Issue with amounts of recommended shows logic')

        const indexInPage = rest.showIndex % count

        const showPageNumber = Math.floor(rest.showIndex / count)

        const isRightOffsetEnough =
          count - indexInPage >= RECOMMENDED_AMOUNT_SHOWS

        const firstRecommendedPage = await tvshows.getLastAddedTvShowsFx({
          page: showPageNumber,
          categoryId,
          collectionId,
          onlyFavorites,
        })

        let secondRecommendedPage: readonly TvShow[] = []
        if (!isRightOffsetEnough && !firstRecommendedPage.last)
          secondRecommendedPage = (
            await tvshows.getLastAddedTvShowsFx({
              page: showPageNumber + 1,
              categoryId,
              collectionId,
              onlyFavorites,
            })
          ).content

        const twoPagesOfShows = [
          ...firstRecommendedPage.content,
          ...secondRecommendedPage,
        ]
        const amount = twoPagesOfShows.length

        if (amount <= RECOMMENDED_AMOUNT_SHOWS_AND_CURRENT)
          return orderRecommended(twoPagesOfShows)
        if (indexInPage + RECOMMENDED_AMOUNT_SHOWS > amount)
          return orderRecommended(
            twoPagesOfShows.slice(
              amount - RECOMMENDED_AMOUNT_SHOWS_AND_CURRENT,
              amount
            )
          )
        if (indexInPage + RECOMMENDED_AMOUNT_SHOWS <= amount)
          return orderRecommended(
            twoPagesOfShows.slice(
              indexInPage,
              indexInPage + RECOMMENDED_AMOUNT_SHOWS + 1
            )
          )
      }

      const tvShows = await tvshows.getLastAddedTvShowsFx({
        page: 0,
        categoryId,
        collectionId,
        onlyFavorites,
        count: RECOMMENDED_AMOUNT_SHOWS_AND_CURRENT,
      })

      return orderRecommended(tvShows.content)
    }
  )

  const getDefaultAllCategory = (): TvShowCategory => ({
    id: 0,
    name: 'All Series',
    sortOrder: 0,
    parentCategoryId: 0,
    tvShowQuantity: 0,
    subCategories: [],
  })
  tvshows.getTvShowCategoryFx.use(async ({ categoryId }) => {
    if (categoryId === 0) return getDefaultAllCategory()

    const category = await payload(
      client.get.api.v3.tvshows.categories(categoryId)()
    )

    return category
  })

  tvshows.initShowsCategoryFx.use(async ({ categoryId, count }) => {
    const category = await tvshows.getTvShowCategoryFx({ categoryId })

    const shows = await tvshows.loadShowsFx({ categoryId, page: 0, count })

    return {
      ...category,
      content: [...shows.content],
    }
  })

  sample({
    clock: tvshows.initShowsSubCategoriesFx,
    target: tvshows.loadParentCategoryFx,
  })

  tvshows.loadSubCategoriesWithShowsFx.use(
    async ({ page, count, categoryId }) => {
      // TODO fix that effect as this one is used as side effect
      const pageOfCategories = await tvshows.loadSubCategoriesShowsFx({
        page,
        count,
        categoryId,
      })
      const categoriesIdsArray = pageOfCategories.content.map(
        (category) => category.id
      )
      const promises = categoriesIdsArray.map((categoryId) =>
        tvshows
          .loadShowsOfSubcategoryFx({
            page: 0,
            categoryId: Number(categoryId),
          })
          .catch(() => ({ content: [] }))
      )
      await Promise.all(promises)
    }
  )

  tvshows.getCategoriesWithShowsFx.use(async ({ page, count }) => {
    const pageOfCategories = await tvshows.getTvShowCategoriesFx({
      page,
      count,
    })
    const categoriesIdsArray = pageOfCategories.content.map(
      (category) => category.id
    )

    const showsPromises = categoriesIdsArray.map((categoryId) =>
      tvshows
        .getShowsByCategoryIdFx({
          page: 0,
          categoryId: Number(categoryId),
        })
        .then((data) => ({
          categoryId: Number(categoryId),
          data,
        }))
        .catch(() => ({ categoryId: Number(categoryId), data: undefined }))
    )

    const showsPages = await Promise.all(showsPromises)

    const formattedCategory = pageOfCategories.content.map(
      (category): CategoryWithShowsApi => {
        const showsPageOfCategory = showsPages.find(
          (shows) => shows.categoryId === category.id
        )?.data

        const isMoreThanOnePage =
          !showsPageOfCategory?.last ||
          showsPageOfCategory?.content.length > DEFAULT_PAGE_SIZE

        const isShowExist = showsPageOfCategory?.content.length
        const pageOfShows: Page<TvShow> = isShowExist
          ? {
              ...showsPageOfCategory,
              content: showsPageOfCategory.content,
            }
          : { content: [], ...getFirstPagePaginateInfo({ totalElements: 0 }) }

        return {
          ...category,
          content: pageOfShows,
          isMoreThanOnePage,
          isMoreThanOnePageSubCategories:
            Number(category.subCategories?.length) > DEFAULT_PAGE_SIZE,
          subCategories: category.subCategories?.slice(0, DEFAULT_PAGE_SIZE),
        }
      }
    )

    return {
      ...pageOfCategories,
      content: formattedCategory,
    }
  })
}
